import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, ProgressBar, Badge, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretUp, faCaretDown } from '@fortawesome/free-solid-svg-icons';

import AdvanceTable from '../../components/base/AdvanceTable';
import AdvanceTableFooter from '../../components/base/AdvanceTableFooter';
import useAdvanceTable from '../../hooks/useAdvanceTable';
import AdvanceTableProvider, {
  useAdvanceTableContext
} from '../../providers/AdvanceTableProvider';
import SearchBox from '../../components/common/SearchBox';

import { http } from '../../features/jobs/api/http';
import { fetchJob } from '../../features/jobs/api/jobsApi';
import { rebuildStandardDataFromJob } from '../../helpers/RebuildStandardDataFromJob';
// The inner sheath derived from the outer, so one toggle replaces two phases of
// typing and the launch still sees both. helpers/tandemPlan.js.
import { INTERIOR, EXTERIOR, tandemGroupsFor } from '../../helpers/tandemPlan';
import CreateWizzardForm from '../../components/project-management/create-new/CreateWizzardForm';
import { checkTandemPair } from '../../helpers/tandemCheck';

// -------------------- utils --------------------
const toNum = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const U = v =>
  String(v ?? '')
    .trim()
    .toUpperCase();

function progressToPercent(p) {
  if (p == null) return 0;
  if (p > 0 && p <= 1) return Math.round(p * 100);
  return Math.round(p);
}

const PHASE_LABEL = {
  conductor_isolation: 'Conductor Isolation',
  rope: 'Rope',
  interior_sheat: 'Interior Sheath',
  exterior_sheat: 'Exterior Sheath',
  coiling: 'Coiling',
  rewinding: 'Rewinding'
};

function phaseBadge(phase) {
  const p = String(phase || '');
  return (
    <Badge
      bg="info"
      text="dark"
      className="rounded-pill px-3 py-2"
      style={{ fontWeight: 800 }}
    >
      {PHASE_LABEL[p] || p || '—'}
    </Badge>
  );
}

function ProgressMini({ pct }) {
  const v = Math.max(0, Math.min(100, toNum(pct)));
  return (
    <div className="d-flex align-items-center gap-2" style={{ minWidth: 120 }}>
      <div className="flex-grow-1">
        <ProgressBar now={v} style={{ height: 7, borderRadius: 999 }} />
      </div>
      <span
        className="text-muted fs-10"
        style={{ minWidth: 34, textAlign: 'right' }}
      >
        {v}%
      </span>
    </div>
  );
}

const NextPhaseTopSection = ({ selectedCount = 0, onStartWizard, busy }) => {
  const { setGlobalFilter } = useAdvanceTableContext();

  return (
    <div className="d-flex flex-wrap gap-3 align-items-center justify-content-between mb-3">
      <SearchBox
        placeholder="Search (job, token, product, order...)"
        onChange={e => setGlobalFilter(e.target.value || undefined)}
        style={{ maxWidth: '28rem' }}
      />

      <div className="d-flex gap-2">
        {selectedCount > 0 && (
          <Button variant="primary" onClick={onStartWizard} disabled={busy}>
            {busy ? 'Loading...' : `Open wizard (${selectedCount})`}
          </Button>
        )}
      </div>
    </div>
  );
};

function firstOrder(ords) {
  return Array.isArray(ords) && ords.length ? ords[0] : null;
}

function formatDueDate(v) {
  if (!v) return '—';
  return String(v).slice(0, 10);
}

// ✅ group key: job + nextPhase (asta rezolvă rope/unwinding/multi-input)
function groupKeyOf(r) {
  const jobId = r?.jobId || '';
  const next = r?.nextPhase?.name || '';
  // Re-route candidates (finished drums redirected to coiling/rewinding) form a
  // SEPARATE group so they don't mix with the drums actually routed there.
  const rr = r?.nextPhase?.reroute ? '::reroute' : '';
  return `${jobId}::${next}${rr}`;
}

function minAvailable(tokens) {
  const arr = (tokens || []).map(t => toNum(t?.availableLength));
  if (!arr.length) return 0;
  return Math.max(0, Math.min(...arr));
}

function tokensSummary(tokens) {
  const list = tokens || [];
  const colors = list
    .map(t => t?.color)
    .filter(Boolean)
    .map(U);

  // ex: "BLUE, BROWN" sau "—"
  const colorStr = colors.length ? colors.join(', ') : '—';
  return {
    count: list.length,
    colors: colorStr
  };
}

// ✅ planning state per group (pentru progress bar verde/roșu/etc.)
function plannedStatus(planned, available) {
  const p = toNum(planned);
  const a = toNum(available);
  if (a <= 0) return { pct: 0, variant: 'secondary', label: '—' };

  const pct = Math.round((p / a) * 100);
  if (p > a)
    return { pct: Math.min(200, pct), variant: 'danger', label: 'Over' };
  if (p === a && p > 0) return { pct: 100, variant: 'success', label: 'OK' };
  if (p > 0 && p < a) return { pct, variant: 'warning', label: 'Partial' };
  return { pct: 0, variant: 'secondary', label: '—' };
}

const NextPhaseWizzard = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const incomingRows = useMemo(() => {
    // First check location state (normal navigation)
    if (location?.state?.rows?.length) return location.state.rows;
    // Fallback: check sessionStorage (Factory 3D new-tab launch)
    try {
      const stored = sessionStorage.getItem('factory3d_wizard_state');
      if (stored) {
        const parsed = JSON.parse(stored);
        sessionStorage.removeItem('factory3d_wizard_state');
        return parsed.rows || [];
      }
    } catch (e) { /* ignore */ }
    return [];
  }, [location?.state?.rows]);

  const [busy, setBusy] = useState(false);

  // phase context state
  const [phaseContext, setPhaseContext] = useState(null);
  const [ctxBusy, setCtxBusy] = useState(false);
  const [ctxErr, setCtxErr] = useState('');

  // ✅ wizard state (CreateWizzardForm)
  const [standardsData, setStandardsData] = useState(null);
  const [wizardGroups, setWizardGroups] = useState([]); // context rows that went into the wizard
  const [wizardLoading, setWizardLoading] = useState(false);
  const [wizardError, setWizardError] = useState('');

  // ✅ planning progress memory (per groupKey)
  // ex: plannedByGroup["job::rope"] = 10000
  const [plannedByGroup, setPlannedByGroup] = useState({});

  // fetch context
  const fetchPhaseContext = useCallback(async rows => {
    const dedupe = new Set();
    const items = [];

    for (const r of rows || []) {
      const jobId = String(r?.jobId || '').trim();
      const tokenId = String(r?.tokenId || '').trim();
      if (!jobId || !tokenId) continue;

      const k = `${jobId}:${tokenId}`;
      if (dedupe.has(k)) continue;
      dedupe.add(k);

      items.push({ jobId, tokenId });
    }

    if (!items.length) {
      setPhaseContext(null);
      return;
    }

    setCtxErr('');
    setPhaseContext(null);

    try {
      setCtxBusy(true);
      const res = await http.post('/api/redis/jobs/next-phase-context', {
        items
      });
      setPhaseContext(res?.data ?? null);
      console.log('next-phase-context response:', res?.data);
    } catch (e) {
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.message ||
        String(e);
      setCtxErr(msg);
    } finally {
      setCtxBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!incomingRows?.length) return;
    fetchPhaseContext(incomingRows);
  }, [incomingRows, fetchPhaseContext]);

  // ✅ GROUP rows (jobId + nextPhase.name)
  const groupedData = useMemo(() => {
    const rows = phaseContext?.rows || [];
    const map = new Map();

    for (const r of rows) {
      const gk = groupKeyOf(r);
      if (!gk) continue;

      if (!map.has(gk)) {
        map.set(gk, {
          groupKey: gk,

          jobId: r.jobId,
          jobCode: r.jobCode,
          productLabel: r.productLabel,
          status: r.status,
          jobProgressPct: progressToPercent(r.progress),

          fromPhase: r.fromPhase,
          nextPhase: r.nextPhase, // {name, dependsOn, parameters, reroute?}
          reroute: !!r?.nextPhase?.reroute,

          tokens: [], // ✅ aici punem toate bobinele/token-urile
          orders: Array.isArray(r.orders) ? r.orders : []
        });
      }

      const g = map.get(gk);
      if (r?.token?.tokenId) {
        g.tokens.push({
          tokenId: r.token.tokenId,
          phase: r.token.phase,
          color: r.token.color || null,
          availableLength: toNum(r.token.availableLength),
          plannedLength: toNum(r.token.plannedLength),
          producedLength: toNum(r.token.producedLength),
          consumedLength: toNum(r.token.consumedLength),
          parents: Array.isArray(r.token.parents) ? r.token.parents : [],
          isPhantom: !!r.token.isPhantom
        });
      }

      // merge orders (dacă ai multiple tokens cu orders diferite)
      const ords = Array.isArray(r.orders) ? r.orders : [];
      for (const o of ords) {
        const exists = (g.orders || []).some(
          x => x?.orderId === o?.orderId && x?.lineId === o?.lineId
        );
        if (!exists) g.orders.push(o);
      }
    }

    // finalize: compute available = min(tokens.availableLength)
    const out = [];
    for (const g of map.values()) {
      const av = minAvailable(g.tokens);
      const sum = tokensSummary(g.tokens);

      out.push({
        ...g,

        availableGroup: av,
        tokenCount: sum.count,
        tokenColors: sum.colors,
        nextPhaseName: g?.nextPhase?.name || '',
        nextPhaseDependsOn: g?.nextPhase?.dependsOn || '',
        nextPhaseReroute: !!g?.reroute
      });
    }

    return out;
  }, [phaseContext]);

  const [data, setData] = useState([]);
  useEffect(() => {
    setData(groupedData);
  }, [groupedData]);

  // ── the order the products get launched in ────────────────────────────────
  //
  // `handleStartWizard` builds `standardData` by walking the groups it is given,
  // and the wizard then queues them in that order — so whatever order this
  // table happens to be in IS the launch order. It was whatever the context
  // endpoint returned, and there was no way to say otherwise: pick three
  // products in Project Token State and the one that mattered most might be
  // queued last.
  //
  // Held as its own list of groupKeys rather than by reordering `data`, because
  // the table is sortable: sorting it to read it must not silently re-order the
  // launch. The position is shown, and only the arrows change it.
  const [launchOrder, setLaunchOrder] = useState([]);

  useEffect(() => {
    setLaunchOrder(prev => {
      const keys = data.map(d => d.groupKey).filter(Boolean);
      const live = new Set(keys);
      // Keep the order already chosen, drop what has gone, append what is new.
      const kept = prev.filter(k => live.has(k));
      const added = keys.filter(k => !kept.includes(k));
      const next = [...kept, ...added];
      // Same list, same identity — or every render re-runs the memos below.
      return next.length === prev.length && next.every((k, i) => k === prev[i])
        ? prev
        : next;
    });
  }, [data]);

  const positionOf = useCallback(
    groupKey => {
      const i = launchOrder.indexOf(groupKey);
      return i < 0 ? launchOrder.length : i;
    },
    [launchOrder]
  );

  /** Move one product one place earlier or later in the launch. */
  const moveInLaunchOrder = useCallback((groupKey, delta) => {
    setLaunchOrder(prev => {
      const i = prev.indexOf(groupKey);
      const j = i + delta;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  }, []);

  const columns = useMemo(() => {
    return [
      {
        id: 'launchOrder',
        header: '#',
        enableSorting: false,
        cell: ({ row: { original } }) => {
          const pos = positionOf(original.groupKey);
          return (
            <div className="d-flex align-items-center gap-1">
              <span className="fw-bold" style={{ minWidth: 16 }}>
                {pos + 1}
              </span>
              <div className="d-flex flex-column">
                <Button
                  variant="link"
                  className="p-0 lh-1 text-700"
                  disabled={pos === 0}
                  title="Launch this one earlier"
                  onClick={() => moveInLaunchOrder(original.groupKey, -1)}
                >
                  <FontAwesomeIcon icon={faCaretUp} />
                </Button>
                <Button
                  variant="link"
                  className="p-0 lh-1 text-700"
                  disabled={pos >= launchOrder.length - 1}
                  title="Launch this one later"
                  onClick={() => moveInLaunchOrder(original.groupKey, 1)}
                >
                  <FontAwesomeIcon icon={faCaretDown} />
                </Button>
              </div>
            </div>
          );
        },
        meta: {
          headerProps: { style: { width: 70 } },
          cellProps: { className: 'py-3' }
        }
      },
      {
        accessorKey: 'productLabel',
        header: 'Product / Group',
        cell: ({ row: { original } }) => (
          <div className="d-flex flex-column">
            <span className="fw-semibold">{original.productLabel || '—'}</span>
            <span className="text-muted fs-10">
              Job: {String(original.jobId || '').slice(0, 8)}…{' '}
              {original.jobCode ? `• ${original.jobCode}` : ''}
            </span>
            <span className="text-muted fs-10">
              Tokens: <span className="fw-semibold">{original.tokenCount}</span>{' '}
              • Colors: {original.tokenColors}
            </span>
          </div>
        ),
        meta: {
          headerProps: { style: { minWidth: 340 } },
          cellProps: { className: 'py-3' }
        }
      },
      {
        accessorKey: 'fromPhase',
        header: 'Current',
        cell: ({ row: { original } }) => phaseBadge(original.fromPhase),
        meta: {
          headerProps: { style: { width: 170 } },
          cellProps: { className: 'py-3' }
        }
      },
      {
        accessorKey: 'availableGroup',
        header: 'Available (group)',
        cell: ({ row: { original } }) => (
          <div className="d-flex flex-column text-end">
            <span className="fw-semibold">
              {toNum(original.availableGroup).toLocaleString()} m
            </span>
            <span className="text-muted fs-10">
              min(available) across tokens
            </span>
          </div>
        ),
        meta: {
          headerProps: { style: { width: 170 }, className: 'text-end' },
          cellProps: { className: 'text-end py-3' }
        }
      },
      {
        accessorKey: 'nextPhaseName',
        header: 'Next phase',
        cell: ({ row: { original } }) => (
          <div className="d-flex flex-column">
            <span className="fw-semibold">
              {PHASE_LABEL[original.nextPhaseName] ||
                original.nextPhaseName ||
                '—'}
              {original.nextPhaseReroute && (
                <Badge bg="warning" text="dark" className="ms-2 fs-10">
                  Re-route
                </Badge>
              )}
            </span>
            <span className="text-muted fs-10">
              {original.nextPhaseReroute
                ? 'finished drum redirected here'
                : `depends on: ${original.nextPhaseDependsOn || '—'}`}
            </span>
          </div>
        ),
        meta: {
          headerProps: { style: { width: 230 } },
          cellProps: { className: 'py-3' }
        }
      },

      // Orders summary
      {
        id: 'orderSummary',
        header: 'Order',
        accessorFn: row => {
          const o = firstOrder(row.orders);
          return `${o?.orderCode || ''} ${o?.client || ''} ${o?.lineId || ''}`;
        },
        cell: ({ row: { original } }) => {
          const o = firstOrder(original.orders);
          if (!o) return <span className="text-muted">—</span>;

          return (
            <div className="d-flex flex-column">
              <div className="d-flex align-items-center gap-2">
                <span className="fw-semibold">{o.orderCode || '—'}</span>
                <Badge bg="secondary" className="rounded-pill">
                  {o.status || '—'}
                </Badge>
              </div>
              <span className="text-muted fs-10">
                Client: {o.client || '—'} • Due: {formatDueDate(o.dueDate)}
              </span>
              <span className="text-muted fs-10">
                Line: {o.lineId || '—'} • Plan:{' '}
                {toNum(o.plannedLength_m).toLocaleString()} m
              </span>
              <span className="text-muted fs-10">
                Delivery: {o.deliveryPlanSummary || '—'}
              </span>
            </div>
          );
        },
        meta: {
          headerProps: { style: { minWidth: 320 } },
          cellProps: { className: 'py-3' }
        }
      },

      // ✅ Planning status bar (verde/roșu)
      {
        id: 'planningStatus',
        header: 'Planning',
        accessorFn: row => row.groupKey,
        cell: ({ row: { original } }) => {
          const planned = toNum(plannedByGroup?.[original.groupKey]);
          const av = toNum(original.availableGroup);
          const st = plannedStatus(planned, av);

          return (
            <div style={{ minWidth: 170 }}>
              <ProgressBar
                now={Math.max(0, Math.min(100, st.pct))}
                variant={st.variant}
                style={{ height: 8, borderRadius: 999 }}
              />
              <div className="text-muted fs-10 mt-1">
                planned:{' '}
                <span className="fw-semibold">{planned.toLocaleString()}</span>{' '}
                / {av.toLocaleString()} m • {st.label}
              </div>
            </div>
          );
        },
        meta: {
          headerProps: { style: { width: 220 } },
          cellProps: { className: 'py-3' }
        }
      },

      {
        accessorKey: 'jobProgressPct',
        header: 'Job progress',
        cell: ({ row: { original } }) => (
          <ProgressMini pct={original.jobProgressPct} />
        ),
        meta: {
          headerProps: { style: { width: 170 } },
          cellProps: { className: 'py-3' }
        }
      }
    ];
  }, [plannedByGroup, positionOf, moveInLaunchOrder, launchOrder.length]);

  const table = useAdvanceTable({
    data,
    columns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: true
  });

  // pre-select all rows when data loads so the user doesn't have to tick them manually
  useEffect(() => {
    if (data.length > 0) {
      table.toggleAllRowsSelected(true);
    }
  }, [data]);

  // In LAUNCH order, not in table order.
  //
  // `getSelectedRowModel` returns them the way the table is currently sorted,
  // which is a reading choice and nothing to do with what should run first.
  const selectedOriginals = useMemo(
    () =>
      table
        .getSelectedRowModel()
        .rows.map(r => r.original)
        .slice()
        .sort((a, b) => positionOf(a.groupKey) - positionOf(b.groupKey)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [table.getSelectedRowModel().rows, positionOf]
  );
  const selectedCount = selectedOriginals.length;

  // ✅ open wizard: fetch full jobs, rebuild standardData, show CreateWizzardForm
  const handleStartWizard = useCallback(async groups => {
    if (!groups || !groups.length) return;

    setWizardError('');
    setWizardLoading(true);

    try {
      // Dedupe job IDs (multiple groups may reference the same job)
      const jobIds = [...new Set(groups.map(g => g.jobId).filter(Boolean))];

      // Fetch full job objects in parallel (we need phasePlan)
      const jobs = await Promise.all(jobIds.map(id => fetchJob(id)));
      const jobMap = {};
      for (const job of jobs) {
        if (job?.jobId) jobMap[job.jobId] = job;
      }

      // Build standardData for each group
      const allStandardData = [];
      for (const g of groups) {
        const job = jobMap[g.jobId];
        if (!job) {
          throw new Error(`Job ${g.jobId} not found or has no phasePlan.`);
        }

        const parentTokenIds = (g.tokens || [])
          .map(t => t?.tokenId)
          .filter(Boolean);
        const nextPhaseName = g.nextPhase?.name || g.nextPhaseName || '';

        // inject phantom tokens into job.tokens so _existingTokens has them
        for (const t of g.tokens || []) {
          if (t?.isPhantom && t?.tokenId?.startsWith('PH-')) {
            job.tokens = job.tokens || {};
            job.tokens[t.tokenId] = {
              tokenId: t.tokenId,
              phase: t.phase,
              status: 'PHANTOM',
              plannedLength: t.availableLength,
              producedLength: t.availableLength,
              availableLength: t.availableLength,
              consumedLength: 0,
              reservedLength: 0,
              progress: 100,
              color: t.color || null,
              parents: [],
              consumedBy: [],
              isPhantom: true
            };
          }
        }

        const rebuilt = rebuildStandardDataFromJob(
          job,
          parentTokenIds,
          nextPhaseName
        );

        // ── WHO ELSE IS ON THE INCOMING DRUMS ────────────────────────────
        //
        // Keyed by TOKEN, because sharing is one fact: the same tokenId under
        // more than one jobId, since it is the same object on the floor. That is
        // the rule Project Token State has always used.
        //
        // The first attempt used `partners`, which is keyed by `mergeQueueKey` —
        // written on every drum launched while a phase had Merge Execution on,
        // whether or not anything merged with it, and carrying only phase +
        // colour + slot. So launches weeks apart looked like partners: the badge
        // read "⇄ shared · 6 products" for three products, and appeared on a rope
        // that was never merged (*"care are project-details … aici nu e merge
        // execution cu nimic"*, 2026-11-03). Measured against the token rule, the
        // same job now reports grey as not shared, blue and brown with two
        // partners each — which is what the floor has.
        let mergePartnersByToken = {};
        try {
          const mp = await http.get(`/api/redis/jobs/${g.jobId}/merge-partners`);
          mergePartnersByToken = mp?.data?.byToken || {};
        } catch {
          /* unavailable — the card simply says nothing, which is correct */
        }

        // Attach group context for submit
        for (const sd of rebuilt) {
          sd._sharedByToken = mergePartnersByToken;
          sd._groupKey = g.groupKey;
          sd._fromPhase = g.fromPhase;
          sd._availableGroup = g.availableGroup;
          sd._orders = g.orders;
          // Baseline phase list of the job — used at submit to diff which phases
          // the user added/deleted in the wizard (persisted via phasePlanEdits).
          sd._originalPhaseNames = (job.phasePlan || [])
            .map(p => p?.name)
            .filter(Boolean);
        }

        allStandardData.push(...rebuilt);
      }

      setWizardGroups(groups);
      setStandardsData(allStandardData);
    } catch (err) {
      console.error('Failed to load wizard data:', err);
      setWizardError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          String(err)
      );
    } finally {
      setWizardLoading(false);
    }
  }, []);

  const clearStandardsData = useCallback(() => {
    setStandardsData(null);
    setWizardGroups([]);
    setWizardError('');
  }, []);

  // ✅ wizard submit: transform standardData back into plan-next-phase payload.
  // In continue mode we plan the precomputed target phase AND any newly-added
  // sibling final (coiling/rewinding) that consumes the same selected drum, and
  // we persist phase add/delete to the job's phasePlan via `phasePlanEdits`.
  const handleWizardSubmit = useCallback(
    async ({
      standardData: wizardData,
      mergedPhases,
      groupByPhases,
      // The other half of a shared bobbin — what the donor keeps and what its
      // lot becomes. Belongs to jobs that are NOT in `groups`, so it travels
      // separately; the wizard used to hand it only to the create-new branch.
      shareEdits = [],
      lotEdits = []
    }) => {
      const phaseHasExec = phase =>
        (Array.isArray(phase?.execution) && phase.execution.length > 0) ||
        (Array.isArray(phase?.conductors) &&
          phase.conductors.some(
            c => Array.isArray(c.execution) && c.execution.length > 0
          ));

      // Build one plan-next-phase group for a single phase of a product.
      const buildGroupForPhase = (
        sd,
        targetPhase,
        execIdToTokenId,
        isChained = false,
        isFirstPhase = false
      ) => {
        const nextPhaseName = targetPhase?.name || '';
        // A chained phase's upstream is another phase planned in THIS submit, so its
        // "from" is the phase it depends on (the backend resolves the real input
        // tokens from those planned outputs). A rooted phase consumes the selected
        // drum, whose phase equals the target's dependsOn. A first-phase re-fill
        // (dependsOn null) has no upstream at all → empty fromPhase.
        const phaseFromPhase = isFirstPhase
          ? ''
          : targetPhase?.dependsOn ?? (sd._fromPhase || '');

        // Build executions from the wizard phase data
        let executions = [];
        if (
          nextPhaseName === 'conductor_isolation' &&
          targetPhase?.conductors
        ) {
          // Conductor isolation: flatten all conductor executions
          for (const cond of targetPhase.conductors) {
            const condColor = cond.isolation?.color || cond.wire?.color || null;
            for (const exec of cond.execution || []) {
              // First-phase re-fill only plans NEW conductors — an exec that
              // already has a tokenId is already planned (e.g. BLUE/BROWN), so
              // never re-submit it (would create a duplicate task).
              if (isFirstPhase && exec.tokenId) continue;
              // Per-execution upstream inputs
              const condExecInputs = [];
              if (exec.fromUpstreamId) {
                const tid = execIdToTokenId[exec.fromUpstreamId];
                if (tid) condExecInputs.push(tid);
                else if (String(exec.fromUpstreamId).startsWith('K-')) condExecInputs.push(exec.fromUpstreamId);
              }
              if (Array.isArray(exec.fromUpstreamIds)) {
                for (const id of exec.fromUpstreamIds) {
                  const tid = execIdToTokenId[id];
                  if (tid) condExecInputs.push(tid);
                  else if (String(id).startsWith('K-')) condExecInputs.push(id);
                }
              }

              executions.push({
                machineId: String(exec.machineId || '').trim(),
                plannedLength: toNum(exec.plannedLength),
                // Which drum this execution shares when the phase is merged.
                // null = let the backend number it positionally, as before.
                // This payload is an explicit whitelist, so without the line
                // the planner's choice is dropped on the way out.
                mergeSlot: exec.mergeSlot ?? null,
                mergeSolo: !!exec.mergeSolo,
                // …and which drum it joins when that drum already exists. Same
                // omission as the flat branch below — see the note there.
                id: exec.id ?? null,
                mergeOnto: exec.mergeOnto ?? null,
                isPhantomSource: !!exec.isPhantomSource,
                execInputTokenIds: condExecInputs.length > 0 ? condExecInputs : null,
                executionMeta: {
                  drumMaterial: exec.drum_material || null,
                  drumDiameter_mm: toNum(exec.drum_diameter) || null,
                  phantomId: exec.phantomId || null,
                  isPhantomSource: !!exec.isPhantomSource,
                  phantomColor: exec.phantomColor || exec.color || condColor || null,
                  conductorColor: condColor,
                  // wizard identity for per-exec chained input linkage (backend
                  // execTokenByWizardId).
                  wizardExecId: exec.id ?? null,
                  wizardUpstreamIds: [
                    ...(exec.fromUpstreamId != null
                      ? [exec.fromUpstreamId]
                      : []),
                    ...(Array.isArray(exec.fromUpstreamIds)
                      ? exec.fromUpstreamIds
                      : [])
                  ].filter(v => v != null),
                  reserveFinal: exec.reserveFinal || {
                    coiling: false,
                    rewinding: false
                  }
                }
              });
            }
          }
        } else {
          // Other phases: execution is at phase level
          for (const exec of targetPhase?.execution || []) {
            if (isFirstPhase && exec.tokenId) continue; // already planned, skip
            let pl = toNum(exec.plannedLength);
            if (nextPhaseName === 'coiling' && exec.coilCount && exec.coilLength) {
              pl = toNum(exec.coilCount) * toNum(exec.coilLength);
            }
            if (nextPhaseName === 'rewinding' && exec.rewindCount && exec.rewindLength) {
              pl = toNum(exec.rewindCount) * toNum(exec.rewindLength);
            }
            // Coils wound straight off the extruder instead of being sent on to a
            // coiler: the run is coilCount × coilLength, exactly like a coiling
            // run, and this was the one case nobody derived — the exec went out
            // at 0 and the server rejected a plan that was perfectly valid.
            // Mirrors getExecTotalLength here and getPlannedLength on the server.
            if (
              nextPhaseName === 'exterior_sheat' &&
              exec.deliveryMode === 'coils_on_machine' &&
              exec.coilCount &&
              exec.coilLength
            ) {
              pl = toNum(exec.coilCount) * toNum(exec.coilLength);
            }
            // Reserve-only with no explicit quantity → reserve the full upstream
            // drum length, so the pending phase covers the whole drum.
            if (!(pl > 0) && exec.reserve) {
              pl = toNum(exec.fromUpstreamLength) || toNum(exec.plannedLength) || 0;
            }

            // Per-execution input tokens (resolve fromUpstreamId/fromUpstreamIds to tokenIds)
            const execInputs = [];
            if (exec.fromUpstreamId) {
              const tid = execIdToTokenId[exec.fromUpstreamId];
              if (tid) execInputs.push(tid);
              else if (String(exec.fromUpstreamId).startsWith('K-')) execInputs.push(exec.fromUpstreamId);
            }
            if (Array.isArray(exec.fromUpstreamIds)) {
              for (const id of exec.fromUpstreamIds) {
                const tid = execIdToTokenId[id];
                if (tid) execInputs.push(tid);
                else if (String(id).startsWith('K-')) execInputs.push(id);
              }
            }

            // Flat cable (MYYUP): the exterior sheath runs off one drum per
            // conductor colour and takes a DIFFERENT number of metres from each
            // — a colour can be spliced from two drums, so a 25 km run may take
            // 20 km from one brown drum and 5 km from the next. Send the per-drum
            // take so the backend reserves exactly that instead of billing every
            // drum it touched for the whole run.
            const execInputTakes = {};
            if (
              nextPhaseName === 'exterior_sheat' &&
              Array.isArray(exec.fromUpstreams)
            ) {
              for (const u of exec.fromUpstreams) {
                const tid = execIdToTokenId[u?.id];
                const take = toNum(u?.take);
                if (tid && take > 0) {
                  execInputTakes[tid] = (execInputTakes[tid] || 0) + take;
                }
              }
            }

            executions.push({
              machineId: String(exec.machineId || '').trim(),
              plannedLength: pl,
              mergeSlot: exec.mergeSlot ?? null,
              mergeSolo: !!exec.mergeSolo,
              // ── JOINING A DRUM THAT ALREADY EXISTS ────────────────────────
              //
              // This payload is a WHITELIST, and `mergeOnto` was not on it. So
              // the wizard offered "Join a drum already on a machine", the
              // preview endpoint validated the division, and the submit dropped
              // the one field that said which bobbin to join — the backend then
              // built a drum of its own. Reported 2026-11-02 on the DTB1250, and
              // the launch came out with a fresh `K-e2017dcf` beside the donor
              // instead of sharing `K-cee56a7d`.
              //
              // `id` goes with it: the backend pairs the share to its execution
              // by `jobId::id`, and the only identity this payload carried was
              // buried in `executionMeta.wizardExecId`.
              id: exec.id ?? null,
              mergeOnto: exec.mergeOnto ?? null,
              execInputTokenIds: execInputs.length > 0 ? execInputs : null,
              execInputTakes: Object.keys(execInputTakes).length
                ? execInputTakes
                : null,
              isPhantomSource: !!exec.isPhantomSource,
              executionMeta: {
                drumMaterial: exec.drum_material || null,
                drumDiameter_mm: toNum(exec.drum_diameter) || null,
                deliveryMode: exec.deliveryMode || null,
                orderItemId: exec.orderItemId || null,
                phantomId: exec.phantomId || null,
                isPhantomSource: !!exec.isPhantomSource,
                phantomColor: exec.phantomColor || null,
                coilLength: toNum(exec.coilLength) || null,
                coilCount: toNum(exec.coilCount) || null,
                rewindLength: toNum(exec.rewindLength) || null,
                rewindCount: toNum(exec.rewindCount) || null,
                // wizard identity for per-exec chained input linkage (backend
                // execTokenByWizardId) — so a chained downstream task (e.g.
                // coiling) consumes ONLY this exec's drum, not all upstream
                // outputs. Also lets each exterior drum trace to its rope drum.
                wizardExecId: exec.id ?? null,
                wizardUpstreamIds: [
                  ...(exec.fromUpstreamId != null ? [exec.fromUpstreamId] : []),
                  ...(Array.isArray(exec.fromUpstreamIds)
                    ? exec.fromUpstreamIds
                    : [])
                ].filter(v => v != null),
                // reserve-only: the user toggled "Reserve" on this coiling/rewinding
                // execution → backend creates the phase with no machine / no queue.
                reserveFinal: {
                  coiling: nextPhaseName === 'coiling' && !!exec.reserve,
                  rewinding: nextPhaseName === 'rewinding' && !!exec.reserve
                }
              }
            });
          }
        }

        // Collect ALL upstream tokenIds from the wizard's phase data. A rooted phase
        // consumes the selected real drum; a chained phase's inputs are filled by the
        // backend from the upstream planned outputs, so we seed nothing here.
        const resolvedTokenIds = new Set(isChained ? [] : (sd._parentTokenIds || []));

        // Collect from this phase's executions (both phase-level and conductor-level)
        const collectUpstreamRefs = (exec) => {
          if (exec.fromUpstreamId) {
            const tid = execIdToTokenId[exec.fromUpstreamId];
            if (tid) resolvedTokenIds.add(tid);
            else if (String(exec.fromUpstreamId).startsWith('K-')) resolvedTokenIds.add(exec.fromUpstreamId);
          }
          if (Array.isArray(exec.fromUpstreamIds)) {
            for (const id of exec.fromUpstreamIds) {
              const tid = execIdToTokenId[id];
              if (tid) resolvedTokenIds.add(tid);
              else if (String(id).startsWith('K-')) resolvedTokenIds.add(id);
            }
          }
        };
        for (const exec of (targetPhase?.execution || [])) collectUpstreamRefs(exec);
        if (targetPhase?.conductors) {
          for (const cond of targetPhase.conductors) {
            for (const exec of (cond.execution || [])) collectUpstreamRefs(exec);
          }
        }

        return {
          action: 'PLAN',
          groupKey: `${sd._parentJobId}::${nextPhaseName}`,
          jobId: sd._parentJobId,
          fromPhase: phaseFromPhase,
          nextPhase: nextPhaseName,
          firstPhase: isFirstPhase || undefined,
          inputTokenIds:
            isChained || isFirstPhase ? [] : [...resolvedTokenIds].filter(Boolean),
          availableGroup: sd._availableGroup || sd.length || 0,
          nextPhaseParameters: targetPhase
            ? {
                name: targetPhase.name,
                type: targetPhase.type,
                material: targetPhase.material,
                density: targetPhase.density,
                diameter: targetPhase.diameter,
                section: targetPhase.section,
                fillingSection: targetPhase.fillingSection,
                minRadial: targetPhase.minRadial,
                maxRadial: targetPhase.maxRadial
              }
            : {},
          executions
        };
      };

      const groups = [];
      const editsByJob = new Map();

      for (const sd of wizardData) {
        const jobId = sd._parentJobId;
        const fromPhase = sd._fromPhase || '';
        const orig = new Set(sd._originalPhaseNames || []);

        // ── phasePlan diff for this job (phases added / deleted in the wizard) ──
        if (jobId) {
          const currentNames = (sd.phases || []).map(p => p?.name).filter(Boolean);
          const currentSet = new Set(currentNames);

          const addedPhases = (sd.phases || [])
            .filter(p => p?.name && !orig.has(p.name))
            .map(p => ({
              name: p.name,
              dependsOn: p.dependsOn ?? null,
              parameters: p.parameters || null
            }));
          const removedPhases = [...orig].filter(n => !currentSet.has(n));

          if (addedPhases.length || removedPhases.length) {
            if (!editsByJob.has(jobId)) {
              editsByJob.set(jobId, { jobId, add: [], remove: [] });
            }
            const e = editsByJob.get(jobId);
            for (const a of addedPhases) {
              if (!e.add.some(x => x.name === a.name)) e.add.push(a);
            }
            for (const r of removedPhases) {
              if (!e.remove.includes(r)) e.remove.push(r);
            }
          }
        }

        // ── exec.id → tokenId map from ALL phase execution entries ──
        const execIdToTokenId = {};
        for (const phase of (sd.phases || [])) {
          if (phase.conductors) {
            for (const cond of phase.conductors) {
              for (const exec of (cond.execution || [])) {
                if (exec.id != null && exec.tokenId) execIdToTokenId[exec.id] = exec.tokenId;
              }
            }
          }
          for (const exec of (phase.execution || [])) {
            if (exec.id != null && exec.tokenId) execIdToTokenId[exec.id] = exec.tokenId;
          }
        }

        // ── which phases to plan this session: the whole filled chain ──
        // Seed with phases that consume the selected drum (dependsOn === fromPhase),
        // then iteratively pull in any phase whose dependsOn is already in the chain
        // (e.g. rope → interior_sheat → exterior_sheat → coiling). Only phases the
        // user actually filled are eligible, so nothing unintended gets planned.
        const filled = (sd.phases || []).filter(p => p?.name && phaseHasExec(p));
        const chainNames = new Set();
        let grew = true;
        while (grew) {
          grew = false;
          for (const p of filled) {
            if (chainNames.has(p.name)) continue;
            const dep = p.dependsOn ?? null;
            if (dep === (fromPhase || null) || chainNames.has(dep)) {
              chainNames.add(p.name);
              grew = true;
            }
          }
        }
        // phasePlan order is already dependency order → upstream before downstream
        const toPlan = (sd.phases || []).filter(p => chainNames.has(p.name));

        for (const phase of toPlan) {
          // chained = its upstream phase is itself being planned in this submit
          const isChained = chainNames.has(phase.dependsOn ?? null);
          const grp = buildGroupForPhase(sd, phase, execIdToTokenId, isChained);
          if (!grp.executions || !grp.executions.length) continue;

          // ── TANDEM: the inner sheath is derived from the outer ───────────
          //
          // With the phase toggle on, the planner filled in the EXTERIOR against
          // the rope and the interior was never touched (so it is not in
          // `toPlan` — it has no executions). Here it is derived: the same
          // machine, the same drums, the same metres, chained so the exterior
          // consumes it.
          //
          // Both groups go to the SAME endpoint the normal route uses, and the
          // job joins `tandemJobs` below — so the launch chains them and the
          // summary's `fuseTandemInteriorExterior` fuses them exactly as it does
          // when both phases were filled in by hand. That is deliberate: the
          // planner asked to compare the two routes, which is only possible if
          // they end in the same place. helpers/tandemPlan.js.
          // The interior's group is built the SAME way every other group is, off
          // the interior phase itself — so it carries the inner sheath's own
          // material, diameter and section. The backend builds `materialPlan`
          // from those, and the fuse unions the two plans: hand it the
          // exterior's parameters and the inner layer's PVC disappears from the
          // job's kg. It has no executions of its own (nothing was filled in);
          // those come from the exterior.
          if (sd?._tandem && phase.name === EXTERIOR) {
            const intPhase = (sd.phases || []).find(p => p?.name === INTERIOR);
            const intBase = intPhase
              ? buildGroupForPhase(
                  sd,
                  intPhase,
                  execIdToTokenId,
                  chainNames.has(intPhase.dependsOn ?? null)
                )
              : null;
            for (const g2 of tandemGroupsFor(grp, intBase)) groups.push(g2);
            continue;
          }

          groups.push(grp);
        }

        // First-phase re-fill: a phase with dependsOn null (e.g. conductor_isolation
        // on a direct CYYF) may have been PARTIALLY planned in an earlier session
        // (some conductors carry a tokenId) and now have newly-added conductors
        // (e.g. GREEN-YELLOW). These have no upstream, so plan them like create-new.
        // Only NEW execs (no tokenId) are submitted; the chain traversal above never
        // includes the source phase itself, so this is the only path that plans them.
        const hasNewExec = phase => {
          const anyNew = execs => (execs || []).some(e => e && !e.tokenId);
          if (anyNew(phase?.execution)) return true;
          return (phase?.conductors || []).some(c => anyNew(c?.execution));
        };
        const firstPhaseFills = (sd.phases || []).filter(
          p =>
            p?.name &&
            (p.dependsOn ?? null) === null &&
            !chainNames.has(p.name) &&
            hasNewExec(p)
        );
        for (const phase of firstPhaseFills) {
          const grp = buildGroupForPhase(sd, phase, execIdToTokenId, false, true);
          if (grp.executions && grp.executions.length > 0) groups.push(grp);
        }
      }

      const phasePlanEdits = [...editsByJob.values()];

      if (!groups.length && !phasePlanEdits.length) {
        setWizardError(
          'Nothing to plan — fill at least one execution, or add/remove a phase.'
        );
        return;
      }

      console.log(
        'ENTER NEXT PHASE WIZZARD',
        groups,
        'phasePlanEdits=',
        phasePlanEdits,
        'mergedPhases=',
        mergedPhases
      );
      // A tandem that does not line up must not be launched.
      //
      // The fusion pairs the two mantles by the upstream link between their
      // executions. When that link is incomplete it deletes the interior tasks
      // it DID match, leaves the rest standing, and the job comes out with
      // orphaned interior tasks and a phase that never reaches 100%. Refusing
      // here costs a re-plan; letting it through costs a repair.
      const tandemProblems = wizardData
        .filter(sd => sd.tandem)
        .map(sd => ({ sd, check: checkTandemPair(sd) }))
        .filter(x => !x.check.ok);

      if (tandemProblems.length) {
        setWizardError(
          'Tandem cannot be launched as planned — ' +
            tandemProblems
              .map(
                x =>
                  `${x.sd.name || 'product'}: ${x.check.problems.join('; ')}`
              )
              .join(' | ') +
            '. Fix it on the review page, or switch Tandem off to launch the two mantles separately.'
        );
        return;
      }

      // Jobs whose interior_sheat+exterior_sheat should be fused into one tandem
      // execution.
      //
      // TWO ways in, on purpose: `sd.tandem` is the per-product toggle on the
      // review page — plan both phases, then fuse — and `sd._tandem` is the one
      // on the phase step, which plans only the exterior and derives the
      // interior above. The summary route is untouched; the new one lands in the
      // same list so the fuse cannot tell them apart (user, 2026-11-03:
      // *"nu renunta la conceptul din summary … vreau sa testez implementarea sa
      // vad daca face acelasi lucru"*).
      const tandemJobs = [
        ...new Set(
          wizardData
            .filter(sd => sd.tandem || sd._tandem)
            .map(sd => sd._parentJobId)
            .filter(Boolean)
            .map(String)
        )
      ];

      // Opt-in reservations made mid-flow (toggle in the Order Reservation card):
      // per-job itemId -> qty, recorded on the order at submit like create-launch.
      const reservations = {};
      for (const sd of wizardData) {
        const jid = sd?._parentJobId ? String(sd._parentJobId) : null;
        const resv = sd?._reservation;
        if (jid && resv && typeof resv === 'object' && Object.keys(resv).length) {
          reservations[jid] = resv;
        }
      }

      const res = await http.post('/api/redis/jobs/plan-next-phase', {
        groups,
        mergedPhases: mergedPhases || {},
        groupByPhases: groupByPhases || {},
        phasePlanEdits,
        tandemJobs,
        reservations,
        // Without these the server has the `mergeOnto` but none of the figures,
        // so the donor keeps whatever it held and the division cannot add up.
        shareEdits,
        lotEdits
      });

      // Surface any rejected phase edits (e.g. can't delete a phase with tasks)
      const rejected = (res?.data?.phasePlanEdits || []).flatMap(
        r => r?.rejected || []
      );
      // …and the drums a DELETED phase sent back to reception. Deleting the
      // coiling phase behind a screenful of CYYF took every one of those drums
      // off `final_product` and out of both doors — refused by reception for
      // having a machine still to visit, and unplannable because the phase was
      // gone (2026-11-05). The routing is put back now; saying so is the other
      // half, because a drum changing door is not a detail the planner should
      // have to go and check.
      const restored = (res?.data?.phasePlanEdits || []).flatMap(
        r => r?.restored || []
      );
      if (rejected.length || restored.length) {
        setWizardError(
          [
            rejected.length &&
              'Some phase edits were rejected: ' +
                rejected.map(x => `${x.name} (${x.reason})`).join(', '),
            restored.length &&
              `${restored.length} drum(s) are the finished product again and go ` +
                'to reception: ' +
                restored.map(x => x.tokenId).join(', ')
          ]
            .filter(Boolean)
            .join('\n')
        );
      }

      // Update planning progress bars
      const add = {};
      for (const g of groups) {
        const totalPlanned = (g.executions || []).reduce(
          (s, e) => s + toNum(e.plannedLength),
          0
        );
        if (g.groupKey) add[g.groupKey] = (add[g.groupKey] || 0) + totalPlanned;
      }
      setPlannedByGroup(prev => {
        const next = { ...prev };
        for (const k of Object.keys(add)) next[k] = (next[k] || 0) + add[k];
        return next;
      });

      // Return to table view + refresh context so new/edited phases reflect
      clearStandardsData();
      if (incomingRows?.length) fetchPhaseContext(incomingRows);

      return res?.data;
    },
    [clearStandardsData, fetchPhaseContext, incomingRows]
  );

  // ✅ WIZARD VIEW: when standardsData is set, render CreateWizzardForm
  if (standardsData) {
    return (
      <CreateWizzardForm
        standardData={standardsData}
        setStandardsData={setStandardsData}
        clearStandardsData={clearStandardsData}
        handleSubmitFc={handleWizardSubmit}
        mode="continue"
      />
    );
  }

  if (!incomingRows?.length) {
    return (
      <Card>
        <Card.Body>
          <h3 className="mb-2">Plan next phase</h3>
          <div className="text-muted">No selected tokens received.</div>
          <Button
            className="mt-3"
            variant="phoenix-secondary"
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
        </Card.Body>
      </Card>
    );
  }

  return (
    <div className="mb-9">
      <AdvanceTableProvider {...table}>
        <div className="d-flex flex-wrap mb-3 gap-3 align-items-center justify-content-between">
          <div>
            <h2 className="mb-0">Plan next phase</h2>
            <div className="text-muted fs-10">
              Groups: {data.length} • Selected: {selectedCount}
            </div>
          </div>

          <div className="d-flex gap-2">
            <Button
              variant="phoenix-secondary"
              onClick={() => navigate(-1)}
              disabled={busy}
            >
              Back
            </Button>
          </div>
        </div>

        {ctxBusy && (
          <Alert variant="info" className="mb-3">
            Loading next-phase-context...
          </Alert>
        )}
        {ctxErr && (
          <Alert variant="warning" className="mb-3">
            next-phase-context error: {ctxErr}
          </Alert>
        )}

        {wizardError && (
          <Alert variant="danger" className="mb-3">
            {wizardError}
          </Alert>
        )}

        <NextPhaseTopSection
          selectedCount={selectedCount}
          onStartWizard={() => handleStartWizard(selectedOriginals)}
          busy={ctxBusy || busy || wizardLoading}
        />

        <div className="border-bottom">
          <AdvanceTable
            tableProps={{
              className: 'phoenix-table border-top border-200 fs-9 table-fixed'
            }}
            // ✅ dacă componenta ta AdvanceTable suportă row events:
            // onRowClick={(row) => handleRowClickOpenWizard(row.original)}
          />
          <AdvanceTableFooter pagination className="py-3" />
        </div>
      </AdvanceTableProvider>
    </div>
  );
};

export default NextPhaseWizzard;
